import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#165581] text-white py-8">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/login" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to eSay
          </Link>
          <h1 className="text-4xl font-bold">Terms of Service</h1>
          <p className="text-white/80 mt-2">Last updated: April 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 space-y-8">
          
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              By accessing or using the eSay email marketing platform ("Service") at esay.co.uk, you agree 
              to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may 
              not access or use the Service.
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              We reserve the right to modify these Terms at any time. We will notify you of significant 
              changes by posting a notice on our website or sending you an email. Your continued use of 
              the Service after such modifications constitutes your acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Description of Service</h2>
            <p className="text-slate-600 leading-relaxed">
              eSay provides an email marketing platform that enables users to:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>Find business leads through public data sources</li>
              <li>Create and manage email templates</li>
              <li>Send email campaigns through connected Gmail accounts</li>
              <li>Track email campaign performance and analytics</li>
              <li>Manage contact lists and subscriptions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Account Registration</h2>
            <p className="text-slate-600 leading-relaxed">
              To use the Service, you must create an account. You agree to:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Promptly update your account information if it changes</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              You must be at least 18 years old to create an account and use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Acceptable Use Policy</h2>
            <p className="text-slate-600 leading-relaxed">
              You agree to use the Service only for lawful purposes and in accordance with these Terms. 
              You agree NOT to:
            </p>
            
            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">4.1 Email Content Restrictions</h3>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>Send spam, unsolicited bulk emails, or emails to purchased/rented lists</li>
              <li>Send emails containing malware, viruses, or malicious content</li>
              <li>Send emails that are fraudulent, deceptive, or misleading</li>
              <li>Send emails containing illegal content or promoting illegal activities</li>
              <li>Send emails that infringe on intellectual property rights</li>
              <li>Send emails containing hate speech, harassment, or threats</li>
              <li>Send emails promoting adult content, gambling, or controlled substances</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">4.2 Compliance Requirements</h3>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>Comply with all applicable anti-spam laws (CAN-SPAM, GDPR, PECR, etc.)</li>
              <li>Only email recipients who have given consent to receive your emails</li>
              <li>Include a valid physical postal address in your emails</li>
              <li>Provide a clear and working unsubscribe mechanism</li>
              <li>Honor unsubscribe requests within 10 business days</li>
              <li>Accurately identify yourself as the sender</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">4.3 Platform Restrictions</h3>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>Attempt to gain unauthorized access to the Service or other accounts</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Use the Service to compete with or replicate our offerings</li>
              <li>Reverse engineer, decompile, or disassemble the Service</li>
              <li>Use automated systems to access the Service without permission</li>
              <li>Resell or redistribute the Service without authorization</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Gmail Integration</h2>
            <p className="text-slate-600 leading-relaxed">
              When you connect your Gmail account to the Service:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>You authorize us to send emails on your behalf through your Gmail account</li>
              <li>You remain responsible for all emails sent through your account</li>
              <li>You must comply with Google's Terms of Service and Acceptable Use Policy</li>
              <li>You may disconnect your Gmail account at any time through the Settings page</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              We do not read, store, or analyze the content of your Gmail inbox. We only use the 
              connection to send emails on your behalf.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Business Lead Search</h2>
            <p className="text-slate-600 leading-relaxed">
              The Service allows you to search for business leads using publicly available information. 
              You acknowledge that:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>Business information is sourced from public directories and may not always be current</li>
              <li>You are responsible for verifying the accuracy of contact information</li>
              <li>You must comply with all applicable laws when contacting businesses</li>
              <li>Search credits are consumed based on your subscription plan</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Subscription Plans and Payment</h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">7.1 Free Plan</h3>
            <p className="text-slate-600 leading-relaxed">
              We offer a free plan with limited features. Free plan users are subject to usage limits 
              on contacts, templates, and campaigns as specified on our pricing page.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">7.2 Paid Plans</h3>
            <p className="text-slate-600 leading-relaxed">
              Paid subscriptions are billed monthly or annually in advance. By subscribing to a paid 
              plan, you authorize us to charge your payment method on a recurring basis.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">7.3 Payment Processing</h3>
            <p className="text-slate-600 leading-relaxed">
              All payments are processed securely through Stripe. We do not store your full credit 
              card details. All prices are in GBP unless otherwise stated.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">7.4 Cancellation and Refunds</h3>
            <p className="text-slate-600 leading-relaxed">
              You may cancel your subscription at any time. Upon cancellation, you will retain access 
              to paid features until the end of your current billing period. We do not provide refunds 
              for partial billing periods.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Intellectual Property</h2>
            <p className="text-slate-600 leading-relaxed">
              The Service and its original content, features, and functionality are owned by eSay and 
              are protected by international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              You retain ownership of all content you create or upload to the Service, including email 
              templates and contact lists. By using the Service, you grant us a limited license to use, 
              store, and process your content solely to provide the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Account Suspension and Termination</h2>
            <p className="text-slate-600 leading-relaxed">
              We reserve the right to suspend or terminate your account if:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>You violate these Terms or our Acceptable Use Policy</li>
              <li>We receive excessive spam complaints about your emails</li>
              <li>Your account shows signs of fraudulent activity</li>
              <li>You fail to pay for your subscription</li>
              <li>Required by law or legal process</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              Upon termination, your right to use the Service will immediately cease. We may delete 
              your account data after a reasonable retention period.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Disclaimer of Warranties</h2>
            <p className="text-slate-600 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              We do not warrant that the Service will be uninterrupted, error-free, or secure. We do 
              not guarantee specific email deliverability rates or results from using the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Limitation of Liability</h2>
            <p className="text-slate-600 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ESAY SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO 
              LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, ARISING FROM YOUR USE OF THE SERVICE.
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              Our total liability for any claims arising from these Terms or your use of the Service 
              shall not exceed the amount you paid us in the twelve (12) months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Indemnification</h2>
            <p className="text-slate-600 leading-relaxed">
              You agree to indemnify and hold harmless eSay, its officers, directors, employees, and 
              agents from any claims, damages, losses, or expenses (including reasonable legal fees) 
              arising from:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>The content of emails you send through the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Governing Law</h2>
            <p className="text-slate-600 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of England 
              and Wales, without regard to its conflict of law provisions. Any disputes arising from 
              these Terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Severability</h2>
            <p className="text-slate-600 leading-relaxed">
              If any provision of these Terms is found to be unenforceable or invalid, that provision 
              shall be limited or eliminated to the minimum extent necessary, and the remaining 
              provisions shall remain in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">15. Contact Information</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <div className="bg-slate-50 rounded-lg p-4 mt-4">
              <p className="text-slate-700 font-medium">eSay</p>
              <p className="text-slate-600">Email: legal@esay.co.uk</p>
              <p className="text-slate-600">Website: https://esay.co.uk</p>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-slate-200 py-6">
        <div className="max-w-4xl mx-auto px-6 text-center text-slate-500 text-sm">
          <p>&copy; {new Date().getFullYear()} eSay. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <Link to="/privacy" className="hover:text-[#165581]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#165581]">Terms of Service</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
